/**
 * Shared utility functions and cell renderers used across
 * the Collabs, Warnings, and Special Cases data tables (MUI DataGrid).
 */
import React, { useEffect } from "react";
import moment from "moment";
import { Tag } from "primereact/tag";
import Langs from "../../app/lang/langs";
import { getFormattedDate, getTagClass, capitalize } from "../../app/utils/helpers";

// ---------------------------------------------------------------------------
// Date formatting helpers
// ---------------------------------------------------------------------------

/**
 * Format a JS Date to a locale string (dd/mm/yyyy style).
 */
export const formatDate = (value) => {
  if (value)
    return value.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  else return null;
};

/**
 * Format a timestamp to "DD-MM-YYYY HH:mm" with a +2h offset (server convention).
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  date.setHours(date.getHours() + 2);
  const isoString = date.toISOString().substring(0, 16);
  const rearranged =
    isoString.substring(8, 10) +
    "-" +
    isoString.substring(5, 7) +
    "-" +
    isoString.substring(0, 4) +
    " " +
    isoString.substring(11, 16);
  return rearranged;
};

// ---------------------------------------------------------------------------
// Reusable cell renderers (for MUI DataGrid renderCell)
// ---------------------------------------------------------------------------

/**
 * Renders a plain date label for a given cell value.
 */
export const renderDateCell = (params) => {
  if (params.value) {
    return <label>{formatDate(params.value)}</label>;
  }
  return null;
};

/**
 * Renders a date label coloured green (future) or red (past).
 */
export const renderColorDateCell = (params) => {
  let textClassName = "";
  if (params.value) {
    if (moment(params.value).isAfter(moment())) {
      textClassName = "text-success";
    } else if (moment(params.value).isBefore(moment())) {
      textClassName = "text-danger";
    }
    return (
      <label className={`${textClassName}`}>
        {formatDate(params.value)}
      </label>
    );
  }
  return null;
};

/**
 * "Created at" cell renderer for warnings — expects row.created_by as an object {nom, prenom}.
 */
export const renderCreatedAtCell = (params) => {
  const row = params.row;
  return (
    <div style={{ lineHeight: 1.4, whiteSpace: "normal", padding: "4px 0" }}>
      <div>{formatTimestamp(row.created_at)} <Langs str="by" /></div>
      <div>
        {row.created_by
          ? `${row.created_by.nom} ${row.created_by.prenom}`
          : "-"}
      </div>
    </div>
  );
};

/**
 * "Created at" cell renderer for special cases — expects flat nom_creator / prenom_creator fields.
 */
export const renderCreatedAtCellSC = (params) => {
  const row = params.row;
  if (
    !row.created_by &&
    !row.nom_creator &&
    !row.prenom_creator
  ) {
    return null;
  }
  return (
    <div style={{ lineHeight: 1.4, whiteSpace: "normal", padding: "4px 0" }}>
      <div>{formatTimestamp(row.created_at)} <Langs str="by" /></div>
      <div>
        {row.created_by
          ? `${row.nom_creator} ${row.prenom_creator}`
          : "-"}
      </div>
    </div>
  );
};

/**
 * "Updated at" cell renderer for special cases.
 */
export const renderUpdatedAtCellSC = (params) => {
  const row = params.row;
  if (
    !row.updated_by &&
    !row.nom_updater &&
    !row.prenom_updater
  ) {
    return null;
  }
  return (
    <div style={{ lineHeight: 1.4, whiteSpace: "normal", padding: "4px 0" }}>
      <div>{formatTimestamp(row.updated_at)} <Langs str="by" /></div>
      <div>
        {row.updated_by
          ? `${row.nom_updater} ${row.prenom_updater}`
          : "-"}
      </div>
    </div>
  );
};

/**
 * "Updated at" cell renderer for warnings.
 */
export const renderUpdatedAtCell = (params) => {
  const row = params.row;
  if (!row.updated_by?.nom && !row.updated_by?.prenom) {
    return null;
  }
  return (
    <div style={{ lineHeight: 1.4, whiteSpace: "normal", padding: "4px 0" }}>
      <div>{formatTimestamp(row.updated_at)} <Langs str="by" /></div>
      <div>
        {row.created_by
          ? `${row.updated_by.nom} ${row.updated_by.prenom}`
          : "-"}
      </div>
    </div>
  );
};

/**
 * Renders multi-line comments as a bulleted list.
 */
export const renderCommentsCell = (params) => {
  if (!params.value) return null;
  const commentsArray = params.value.split("\n");
  return (
    <ul style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
      {commentsArray.map(
        (comment, index) =>
          comment.trim() !== "" && (
            <li
              key={index}
              style={{
                position: "relative",
                paddingLeft: "20px",
                wordWrap: "break-word",
                maxWidth: "35ch",
                marginBottom: "8px",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: "0",
                  top: "0",
                }}
              >
                &bull;
              </span>
              {comment}
            </li>
          )
      )}
    </ul>
  );
};

/**
 * Warning date cell renderer — falls back to start_date if warning_date is missing.
 */
export const renderWarningDateCell = (params) => {
  const row = params.row;
  if (row.warning_date) {
    return (
      <label className="">
        {getFormattedDate(row.warning_date)}
      </label>
    );
  } else if (row.start_date) {
    return (
      <label className="">
        {getFormattedDate(row.start_date)}
      </label>
    );
  }
  return null;
};

/**
 * Renders a Tag for warning / special case category.
 */
export const renderCategoryCell = (params) => {
  let severity = getTagClass(params.value);
  return <Tag value={<Langs str={params.value} />} severity={severity} />;
};

/**
 * Renders a Tag for special case type.
 */
export const renderSpecialCaseCell = (params) => {
  let severity = getTagClass(params.value);
  return <Tag value={<Langs str={params.value} />} severity={severity} />;
};

/**
 * Renders a Tag for warning status.
 */
export const renderWarningStatusCell = (params) => {
  return (
    <Tag
      severity={getTagClass(params.value)}
      value={capitalize(params.value)}
    />
  );
};

// ---------------------------------------------------------------------------
// Single-select filter operator for MUI DataGrid
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Pinned actions column hook (community edition workaround)
// ---------------------------------------------------------------------------

/**
 * Keeps the "actions" column visually pinned to the right edge of the DataGrid
 * by applying a translateX on scroll. Works around the lack of column pinning
 * in @mui/x-data-grid community edition.
 */
export function usePinnedActionsColumn(apiRef) {
  useEffect(() => {
    let scroller = null;
    let mutationObs = null;
    let resizeObs = null;
    let retryTimer = null;

    const applyPin = () => {
      if (!scroller) return;
      const { scrollLeft, clientWidth, scrollWidth } = scroller;
      const offset = scrollLeft + clientWidth - scrollWidth;
      const needsPin = offset < -1;

      const root = apiRef?.current?.rootElementRef?.current;
      if (!root) return;

      const elements = root.querySelectorAll(
        '.MuiDataGrid-cell[data-field="actions"], .MuiDataGrid-columnHeader[data-field="actions"]'
      );

      elements.forEach((el) => {
        if (needsPin) {
          el.style.transform = `translateX(${offset}px)`;
          el.style.position = "relative";
          el.style.zIndex = "4";
          el.style.backgroundColor = "var(--surface-card, #fff)";
          el.style.boxShadow = "-3px 0 4px -2px rgba(0,0,0,0.1)";
        } else {
          el.style.transform = "";
          el.style.position = "";
          el.style.zIndex = "";
          el.style.boxShadow = "";
        }
      });
    };

    const setup = () => {
      const root = apiRef?.current?.rootElementRef?.current;
      if (!root) return false;

      scroller = root.querySelector(".MuiDataGrid-virtualScroller");
      if (!scroller) return false;

      // Horizontal + vertical scroll → re-pin (vertical scroll creates new row DOM)
      scroller.addEventListener("scroll", applyPin);

      // Detect new action cells added by row virtualization
      mutationObs = new MutationObserver(applyPin);
      mutationObs.observe(root, { childList: true, subtree: true });

      // Resize → re-pin
      resizeObs = new ResizeObserver(applyPin);
      resizeObs.observe(scroller);

      requestAnimationFrame(applyPin);
      return true;
    };

    // Retry until grid is mounted
    if (!setup()) {
      let attempts = 0;
      retryTimer = setInterval(() => {
        attempts++;
        if (setup() || attempts > 50) clearInterval(retryTimer);
      }, 100);
    }

    return () => {
      if (retryTimer) clearInterval(retryTimer);
      if (scroller) scroller.removeEventListener("scroll", applyPin);
      if (mutationObs) mutationObs.disconnect();
      if (resizeObs) resizeObs.disconnect();
    };
  }, [apiRef]);
}

// ---------------------------------------------------------------------------
// Single-select filter operator for MUI DataGrid
// ---------------------------------------------------------------------------

/**
 * Single-select filter operator for MUI DataGrid.
 * @param {Array} options - Array of { value, label } objects
 */
export const singleSelectFilterOperator = (options) => ({
  label: "is",
  value: "is",
  getApplyFilterFn: (filterItem) => {
    if (!filterItem.value) return null;
    return (value) => value === filterItem.value;
  },
  InputComponent: ({ item, applyValue }) => (
    <select
      value={item.value || ""}
      onChange={(e) => applyValue({ ...item, value: e.target.value || undefined })}
      style={{
        width: "100%",
        padding: "4px 0",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.42)",
        background: "transparent",
        fontSize: "1rem",
        fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
        outline: "none",
        cursor: "pointer",
        height: 28,
        alignSelf: "flex-end",
      }}
    >
      <option value="">All</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  ),
});
